// @ts-nocheck
/**
 * The cross-event view behind "All events" on the Live Sheet tab row.
 *
 * Different scope from the per-event tabs on purpose: one line per event with
 * the money and the teams, so a director can see the season at a glance.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { Table2 } from "lucide-react";
import { formatCalendarDate } from "@/lib/dates";

const money = (v: unknown) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(v ?? 0) || 0
  );

export default function AllEventsSheetPage() {
  const { scopeOrgId, ready } = useAdminOrgScope();
  const [search, setSearch] = useState("");

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["all-events-sheet", scopeOrgId],
    enabled: ready,
    queryFn: async () => {
      let q = (supabase as any)
        .from("v_event_admin_dashboard")
        .select("*")
        .order("start_date", { ascending: false });
      if (scopeOrgId) q = q.eq("organization_id", scopeOrgId);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? events.filter((e: any) => String(e.name ?? "").toLowerCase().includes(q))
      : events;
  }, [events, search]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc: any, e: any) => ({
          teams: acc.teams + Number(e.total_registrations ?? 0),
          paid: acc.paid + Number(e.paid_registrations ?? 0),
          revenue: acc.revenue + Number(e.total_collected ?? 0),
          outstanding: acc.outstanding + Number(e.total_outstanding ?? 0),
        }),
        { teams: 0, paid: 0, revenue: 0, outstanding: 0 }
      ),
    [rows]
  );

  return (
    <div className="space-y-4 px-4 pt-6 md:px-6 md:pt-10 lg:px-8">
      <div className="flex items-center gap-2">
        <Table2 className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-heading text-2xl font-bold">All events</h1>
          <p className="text-sm text-muted-foreground">
            Revenue, teams and what's still owed, across every event.
          </p>
        </div>
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search events…"
        className="h-9 w-72 rounded-md border border-input bg-background px-3 text-sm"
      />

      <div className="overflow-auto rounded-md border">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-muted">
            <tr>
              {["Event", "Dates", "Teams", "Paid", "Revenue", "Outstanding", ""].map((h) => (
                <th key={h} className="border-b px-3 py-2 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {rows.map((e: any) => (
              <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="px-3 py-2 font-medium">{e.name}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {formatCalendarDate(e.start_date, "M/d/yyyy", "—")}
                </td>
                <td className="px-3 py-2">{e.total_registrations ?? 0}</td>
                <td className="px-3 py-2">{e.paid_registrations ?? 0}</td>
                <td className="px-3 py-2">{money(e.total_collected)}</td>
                <td className="px-3 py-2">{money(e.total_outstanding)}</td>
                <td className="px-3 py-2">
                  <Link
                    to={`/admin-dashboard/events/${e.id}/teams?view=spreadsheet`}
                    className="text-primary underline"
                  >
                    Open sheet
                  </Link>
                </td>
              </tr>
            ))}
            {!isLoading && rows.length > 0 && (
              <tr className="bg-muted/40 font-semibold">
                <td className="px-3 py-2" colSpan={2}>
                  Total
                </td>
                <td className="px-3 py-2">{totals.teams}</td>
                <td className="px-3 py-2">{totals.paid}</td>
                <td className="px-3 py-2">{money(totals.revenue)}</td>
                <td className="px-3 py-2">{money(totals.outstanding)}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
